import { useRef, type ReactElement, type ReactNode } from "react";

// A small, accessible tab bar (WAI-ARIA tabs pattern): roving tabindex,
// left/right/home/end keyboard nav, aria-selected. Controlled — the parent owns
// the active id and renders the matching panel (see TabPanel below).

export interface TabDef {
  id: string;
  label: string;
  icon?: string; // optional Font Awesome class, e.g. "fas fa-user"
  // Count of items awaiting action on that tab. Rendered only when > 0, and
  // announced in the tab's accessible name so it isn't a sighted-only signal.
  badge?: number;
}

// The panel paired with a tab: one place for the id/aria wiring the tab bar's
// aria-controls points at (previously copy-pasted into every tab component).
// `card` (the default) renders the usual single-card panel; `card={false}` is
// the multi-card variant (.tab-panel restores the column gap the wrapper would
// otherwise swallow). `title` is the card's heading, when the panel has one.
export function TabPanel({
  id,
  title,
  card = true,
  children,
}: {
  id: string;
  title?: ReactNode;
  card?: boolean;
  children: ReactNode;
}): ReactElement {
  return (
    <section
      className={card ? "app-card" : "tab-panel"}
      id={`panel-${id}`}
      role="tabpanel"
      aria-labelledby={`tab-${id}`}
    >
      {title ? <h2>{title}</h2> : null}
      {children}
    </section>
  );
}

export function Tabs({
  tabs,
  active,
  onSelect,
  label = "Dashboard sections",
}: {
  tabs: TabDef[];
  active: string;
  onSelect: (id: string) => void;
  label?: string;
}): ReactElement {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  function onKeyDown(e: React.KeyboardEvent, index: number): void {
    const last = tabs.length - 1;
    let next: number | null = null;
    if (e.key === "ArrowRight") next = index === last ? 0 : index + 1;
    else if (e.key === "ArrowLeft") next = index === 0 ? last : index - 1;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = last;
    const target = next === null ? undefined : tabs[next];
    if (next !== null && target) {
      e.preventDefault();
      onSelect(target.id);
      refs.current[next]?.focus();
    }
  }

  return (
    <div className="tabs" role="tablist" aria-label={label}>
      {tabs.map((tab, i) => {
        const selected = tab.id === active;
        return (
          <button
            key={tab.id}
            ref={(el) => {
              refs.current[i] = el;
            }}
            role="tab"
            id={`tab-${tab.id}`}
            aria-selected={selected}
            aria-controls={`panel-${tab.id}`}
            tabIndex={selected ? 0 : -1}
            className={`tab-btn${selected ? " active" : ""}`}
            onClick={() => onSelect(tab.id)}
            onKeyDown={(e) => onKeyDown(e, i)}
          >
            {tab.icon ? <i className={tab.icon} aria-hidden="true" /> : null}
            {tab.label}
            {tab.badge ? (
              <span className="tab-badge" aria-label={`${tab.badge} awaiting review`}>
                {tab.badge}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
